/**
 * MapCanvas — MapLibre instance + setupMapLayers. Корень всех data layers.
 *
 * Жизненный цикл:
 *  1. mount: создаём maplibre-gl Map, базовый style (Esri imagery + OSM streets fallback)
 *  2. on 'load': setupMapLayers (добавляем 5 sources + 12 layers ОДИН раз)
 *  3. подписываем sync hooks (mode/date/crop patterns/expression/focus/toggles)
 *  4. на click по полю — извлекаем properties из MVT feature → setSelectedFieldId
 *  5. unmount: map.remove(), все источники GC'тся
 *
 * НЕ переинициализирует style — все слои добавляются программно.
 */
import { useEffect, useRef, useState } from 'react';
import maplibregl, { type Map as MLMap, type MapLayerMouseEvent } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import './map.css';
import { useCropsReference } from '@/lib/map/useCropsReference';
import { useMapStore } from '@/lib/map/useMapStore';
import {
  setupMapLayers,
  LAYER_IDS,
  recreateFieldsSource,
  applyScopeFilter,
  applySelectedFieldHighlight,
  updateRasterTileUrls,
} from '@/lib/map/setupMapLayers';
import { api } from '@/lib/api';
import { dispatchScopeChanged, useScopeChanged } from '@/lib/scopeEvent';
import {
  useNdviModeSync,
  useDateSync,
  useCropPatternsSync,
  useCropFillExpressionSync,
  useFocusModeSync,
  useCropIconsToggleSync,
  useCropNameToggleSync,
  useFieldNumberToggleSync,
  useFieldAreaToggleSync,
  useCropFillSync,
  useTCIToggleSync,
  useCloudMaskToggleSync,
} from '@/lib/map/syncHooks';
import { useBasemapSync } from '@/lib/map/useBasemapSync';
import { getMapInit, type MapInitData } from '@/lib/map/mapInitCache';
import { userLevelFromMap } from '@/lib/map/zoomLevels';
import { useAvailableDates } from '@/lib/map/useAvailableDates';
import { useFarmChips } from './useFarmChips';
import type { FieldDetail } from './FieldDetailPopover';
import { cn } from '@/lib/utils';
import { captureException } from '@/lib/sentry';
import { IMAGERY_TILE_URL, TRANSPORT_TILE_URL } from '@/lib/map/basemapUrls';
import { fetchScopeAvailable } from '@/lib/http/sharedQueries';

interface MapCanvasProps {
  className?: string;
  initialCenter?: [number, number];
  initialZoom?: number;
  onFieldClick?: (field: FieldDetail, screenPos: { x: number; y: number }) => void;
}

// Перф-аудит 14.08 (корень 1): через наш nginx-прокси, не напрямую в Esri.
const ESRI_IMAGERY = IMAGERY_TILE_URL();
const ESRI_TRANSPORT = TRANSPORT_TILE_URL();

// Hit layers — все включают fieldsHit (always-visible invisible polygon),
// чтобы click внутри поля сработал даже когда все NDVI/crop fill скрыты.
// Остальные нужны как fallback (если fieldsHit временно убран отладкой).
const FIELDS_HIT_LAYERS = [
  LAYER_IDS.fieldsHit,
  LAYER_IDS.cropFillPlanned,
  LAYER_IDS.cropFillGrowing,
  LAYER_IDS.cropFillHarvested,
  LAYER_IDS.uniformity,
  LAYER_IDS.avgNdvi,
  LAYER_IDS.fieldsOutline,
];

export function MapCanvas({
  className,
  initialCenter = [42.5, 46.9],
  initialZoom = 7,
  onFieldClick,
}: MapCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MLMap | null>(null);
  const [map, setMap] = useState<MLMap | null>(null);
  const [mapInit, setMapInit] = useState<MapInitData | null>(null);
  const [companyRevealed, setCompanyRevealed] = useState(false);
  // userLevel — INVERTED scale 1..12 (production semantics, см. zoomLevels.ts):
  //   12 = САМЫЙ ДАЛЁКИЙ zoom (ML 5.3, 100km) → company chip
  //   7  = средний (ML 9.6, 5km)   → farm chips
  //   1  = САМЫЙ БЛИЗКИЙ (ML 16, 50m) → individual fields
  // НЕ использовать map.getZoom() напрямую — это даёт ML zoom (другая шкала).
  const [userLevel, setUserLevel] = useState(7);

  const selectedDate = useMapStore((s) => s.selectedDate);
  const mode = useMapStore((s) => s.mode);
  const focusedCropId = useMapStore((s) => s.focusedCropId);
  const cropIconsOn = useMapStore((s) => s.cropIconsOn);
  const cropNameOn = useMapStore((s) => s.cropNameOn);
  const fieldNumberOn = useMapStore((s) => s.fieldNumberOn);
  const fieldAreaOn = useMapStore((s) => s.fieldAreaOn);
  const cropColorFillOn = useMapStore((s) => s.cropColorFillOn);
  const showTCI = useMapStore((s) => s.showTCI);
  const showCloudMask = useMapStore((s) => s.showCloudMask);
  const basemap = useMapStore((s) => s.basemap);
  const activeScope = useMapStore((s) => s.activeScope);
  const setActiveScope = useMapStore((s) => s.setActiveScope);

  // Локальные cluster/farm-фильтры — derived из activeScope. Передаются в
  // useFarmChips и используются для рекреации MVT source.
  const clusterFilter = activeScope.type === 'cluster' ? activeScope.id : null;
  const farmFilter = activeScope.type === 'farm' ? activeScope.id : null;

  const crops = useCropsReference();

  // Загружаем агрегированные маркеры (companies/clusters/farms/sub_markers)
  // из /api/v1/fields/map-init. Используются useFarmChips для HTML-чипов
  // которые позволяют "схлопнуть" поля в маркеры по фермам/кластерам при
  // zoom out (производственный паттерн из старого frontend/).
  useEffect(() => {
    let cancelled = false;
    void getMapInit()
      .then((data) => {
        if (!cancelled) setMapInit(data);
      })
      .catch((e) => {
        if (import.meta.env.DEV) console.warn('[map] map-init load failed', e);
        else captureException(e, { tags: { source: 'MapCanvas.mapInit' } });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-select подходящей даты под текущий mode.
  //
  // QA Round 5: default selectedDate = today, но available-dates latest <today
  // → растр endpoint возвращает пустой 334-byte PNG.
  //
  // AK-77: GNR-режим использует свой список дат (pipeline иногда пропускает
  // GNR.cog). Если selectedDate из NDVI-списка отсутствует среди GNR-дат —
  // /scene-gnr возвращает пустой PNG, MapLibre кэширует "tile not loaded" и
  // больше запросов не делает → founder видит пустую карту.
  // Решение: подписываемся на оба списка дат + текущий mode и при mismatch'е
  // переключаемся на ближайшую доступную дату для активного индекса.
  const ndviDatesForSync = useAvailableDates({ indexCode: 'ndvi', scope: activeScope });
  const gnrDatesForSync = useAvailableDates({ indexCode: 'gnr', scope: activeScope });
  useEffect(() => {
    const datesForMode = mode === 'gnr' ? gnrDatesForSync : ndviDatesForSync;
    if (!datesForMode || datesForMode.length === 0) return;
    const current = useMapStore.getState().selectedDate;
    // Если текущая дата уже валидна для активного режима — ничего не делаем.
    if (current && datesForMode.includes(current)) return;
    // Иначе берём последнюю доступную (ISO sort гарантирует chronological order).
    const latest = datesForMode[datesForMode.length - 1];
    if (latest && latest !== current) {
      useMapStore.getState().setSelectedDate(latest);
    }
  }, [mode, ndviDatesForSync, gnrDatesForSync]);

  // useFarmChips — HTML-overlay markers (company / cluster / farm chips +
  // DBSCAN sub-markers). Уровни видимости (zoom-driven) и anti-collision
  // dedupe реализованы внутри хука. Активируется когда mapInit приедет.
  // persistScope — клик по чипу (cluster/farm/company). Делает 2 вещи:
  //   1) POST /api/v1/scope/set — серверный active_scope (для других виджетов
  //      Dashboard, которые подписаны через useScopeChanged).
  //   2) dispatchScopeChanged — наш local listener ниже обновит activeScope в store
  //      → это триггерит useEffect ниже который пересоздаст MVT source +
  //      сделает fitBounds к bbox выбранного объекта.
  // Mirror: frontend/src/app/map/page.tsx:246
  const persistScope = (type: 'company' | 'cluster' | 'farm', id: string | null) => {
    api.post('/api/v1/scope/set', { scope_type: type, scope_id: id }).catch((e) => {
      if (import.meta.env.DEV) console.error('[scope/persist] failed', e);
      else captureException(e, { tags: { source: 'MapCanvas.persistScope' } });
    });
    dispatchScopeChanged({ scope_type: type, scope_id: id });
  };

  // Слушаем scope-changed event (от persistScope ИЛИ от переключателя
  // «Хозяйство ▾» в сайдбаре — `FarmSwitcher` → `useScopeSetter`).
  // Один путь обновления → state в store → пересоздание source/fitBounds.
  useScopeChanged((detail) => {
    setActiveScope({ type: detail.scope_type, id: detail.scope_id });
  });

  // При mount читаем серверный current_scope — чтобы при reload карта восстановила
  // тот же фильтр, что показывает переключатель хозяйства (single source of
  // truth — backend).
  useEffect(() => {
    let cancelled = false;
    fetchScopeAvailable<{
      current: { scope_type: 'company' | 'cluster' | 'farm'; scope_id: string | null };
    }>()
      .then((data) => {
        if (cancelled || !data?.current) return;
        const cur = data.current;
        const local = useMapStore.getState().activeScope;
        // Не overwrite, если уже совпадает — иначе триггерится useEffect с fitBounds.
        if (local.type !== cur.scope_type || local.id !== cur.scope_id) {
          setActiveScope({ type: cur.scope_type, id: cur.scope_id });
        }
      })
      .catch(() => {
        /* tolerate — fallback к default 'company' */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFarmChips({
    mapRef,
    markers: mapInit?.markers ?? [],
    clusterMarkers: mapInit?.cluster_markers ?? [],
    subMarkers: mapInit?.sub_markers ?? [],
    farmFilter,
    clusterFilter,
    userLevel,
    companies: mapInit?.companies ?? [],
    companyRevealed,
    setCompanyRevealed,
    persistScope,
  });

  // Smena scope (cluster/farm/company) → пересоздаём MVT source с новым URL
  // (?cluster_id=...|?farm_id=...) + fitBounds к bbox выбранного объекта.
  // Production-pattern из frontend/src/app/map/page.tsx (filter-apply useEffect).
  //
  // Founder bug 2026-05-04: «при выборе хозяйства карта не зумится».
  // Причина: lastScopeKeyRef ранее инициализировался persisted state → на mount
  // scopeKey === lastRef → fitBounds skip. Возврат к initial '' → mount всегда
  // делает fitBounds. Throttle через lastFitTimeRef (>500ms gap) защищает от
  // double-fire когда GET /scope/available re-set'ит scope тем же значением.
  const lastScopeKeyRef = useRef<string>('');
  const lastFitTimeRef = useRef<number>(0);
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;

    const scopeKey = `${activeScope.type}:${activeScope.id ?? ''}`;
    const scopeChanged = lastScopeKeyRef.current !== scopeKey;
    lastScopeKeyRef.current = scopeKey;
    if (!scopeChanged) return;
    // Throttle применяется ТОЛЬКО к fitBounds (anti-double-zoom),
    // recreateFieldsSource + applyScopeFilter + updateRasterTileUrls
    // должны выполняться всегда — иначе тайлы остаются от предыдущего scope.
    const now = Date.now();
    const skipFitBounds = now - lastFitTimeRef.current < 500;
    lastFitTimeRef.current = now;

    const scope =
      activeScope.type === 'company' ? null : { type: activeScope.type, id: activeScope.id };

    const apply = () => {
      // 1. Пересоздаём source — старые in-memory тайлы выкидываются,
      //    новые тянутся с уже отфильтрованным набором полей.
      try {
        recreateFieldsSource(m, useMapStore.getState().selectedDate, scope);
      } catch (e) {
        if (import.meta.env.DEV) console.warn('[map/scope] recreate source failed', e);
      }

      // 1b. Дополнительная страховка — client-side filter на outline/fill/labels.
      // Даже если backend вернёт лишние feature (cache, etc.), MapLibre их
      // не отрисует. Фильтр накладывается на farm_id / cluster_id properties
      // которые backend добавил в MVT.
      try {
        applyScopeFilter(m, scope);
      } catch (e) {
        if (import.meta.env.DEV) console.warn('[map/scope] applyScopeFilter failed', e);
      }

      // 1c. AK-71: пересоздаём URL'ы raster sources (NDVI/GNR/TCI + contrast)
      // с новым scope-clip — backend клипит растры по геометрии scope, что
      // экономит traffic и фокусирует визуализацию только на полях scope.
      // cloudMask остаётся без scope (full scene — погодный контекст).
      // Note: useDateSync ниже тоже подписан на scope (через scopeKey deps),
      // но он сработает в следующем render-цикле; здесь делаем сразу для
      // мгновенной реакции UI без race.
      try {
        updateRasterTileUrls(m, useMapStore.getState().selectedDate, scope);
      } catch (e) {
        if (import.meta.env.DEV) console.warn('[map/scope] updateRasterTileUrls failed', e);
      }

      // 2. fitBounds к bbox выбранного объекта (если знаем).
      let bbox: [number, number, number, number] | null | undefined = null;
      if (activeScope.type === 'farm' && activeScope.id) {
        bbox = mapInit?.markers.find((mm) => mm.id === activeScope.id)?.bbox ?? null;
      } else if (activeScope.type === 'cluster' && activeScope.id) {
        bbox = mapInit?.cluster_markers.find((c) => c.id === activeScope.id)?.bbox ?? null;
      } else if (activeScope.type === 'company') {
        // Вся компания — fit на union всех clusterMarkers bbox.
        const all = mapInit?.cluster_markers ?? [];
        if (all.length > 0) {
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for (const c of all) {
            if (!c.bbox) continue;
            const [x1, y1, x2, y2] = c.bbox;
            if (x1 < minX) minX = x1;
            if (y1 < minY) minY = y1;
            if (x2 > maxX) maxX = x2;
            if (y2 > maxY) maxY = y2;
          }
          if (Number.isFinite(minX)) bbox = [minX, minY, maxX, maxY];
        }
      }
      if (bbox && !skipFitBounds) {
        try {
          // Founder feedback 2026-05-04: «максимально большой кадр».
          // maxZoom 15/12/9 (было 13/11/9) + padding 40 (было 80) — поля
          // занимают больше viewport, видны крупнее.
          const maxZoom =
            activeScope.type === 'farm' ? 15 : activeScope.type === 'cluster' ? 12 : 9;
          m.fitBounds(
            [
              [bbox[0], bbox[1]],
              [bbox[2], bbox[3]],
            ],
            { padding: 40, maxZoom, duration: 700, essential: true },
          );
        } catch (e) {
          if (import.meta.env.DEV) console.warn('[map/scope] fitBounds failed', e);
        }
      }
    };

    if (m.isStyleLoaded()) apply();
    else m.once('load', apply);
  }, [activeScope, mapInit]);

  // Init map ONCE
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    (maplibregl as unknown as { maxParallelImageRequests: number }).maxParallelImageRequests = 32;

    const mapInstance = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
        sources: {
          base: {
            type: 'raster',
            tiles: [ESRI_IMAGERY],
            tileSize: 256,
            attribution: 'Esri · Maxar · Earthstar Geographics',
          },
          'roads-overlay': {
            type: 'raster',
            tiles: [ESRI_TRANSPORT],
            tileSize: 256,
          },
        },
        layers: [
          { id: 'base', type: 'raster', source: 'base' },
          { id: 'roads-overlay', type: 'raster', source: 'roads-overlay' },
        ],
      },
      center: initialCenter,
      zoom: initialZoom,
      minZoom: 4,
      maxZoom: 18,
      attributionControl: false,
      fadeDuration: 0,
      renderWorldCopies: false,
      // Защита от basic-auth credentials в URL: когда staging закрыт
      // basic-auth gate'ом, location.href содержит "stage:password@..." →
      // MapLibre резолвит относительные tile URLs через эту базу и наследует
      // credentials → MapLibre блокирует с "URL is not valid or contains user credentials".
      // Strip credentials из любого URL перед запросом.
      transformRequest: (url) => {
        try {
          const parsed = new URL(url, window.location.href);
          if (parsed.username || parsed.password) {
            parsed.username = '';
            parsed.password = '';
            return { url: parsed.toString() };
          }
        } catch {
          // Если URL не парсится — отдаём как есть, MapLibre сам сообщит
        }
        return { url };
      },
    });

    mapInstance.addControl(
      new maplibregl.AttributionControl({ compact: true }),
      'bottom-right',
    );
    mapInstance.addControl(
      new maplibregl.ScaleControl({ unit: 'metric' }),
      'bottom-right',
    );
    // Zoom +/- only — компас убран (founder QA 2026-05-01: «не работает и не нужен»).
    mapInstance.addControl(
      new maplibregl.NavigationControl({
        showCompass: false,
        showZoom: true,
        visualizePitch: false,
      }),
      'top-right',
    );

    // Кастомный control «🏠 Вернуться к выбранному scope» — заменил компас.
    // При клике triggers fitBounds к bbox активного scope (cluster/farm/company).
    // useEffect [activeScope] выше уже знает как это делать — переиспользуем
    // через scope-changed event с тем же scope (no-op для server, но бамп
    // lastScopeKeyRef через scopeKey-сравнение пропустит → форсим через
    // setActiveScope-replay).
    const homeBtn = document.createElement('button');
    homeBtn.type = 'button';
    homeBtn.className = 'maplibregl-ctrl-icon';
    homeBtn.title = 'Вернуться к выбранному объекту';
    homeBtn.setAttribute('aria-label', 'Вернуться к выбранному объекту');
    homeBtn.style.cssText = 'display:flex;align-items:center;justify-content:center;';
    // Простой home/target SVG icon (24x24 currentColor stroke).
    homeBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/><line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/></svg>';
    homeBtn.onclick = () => {
      // Replay current scope чтобы триггернуть useEffect (lastScopeKeyRef
      // уже содержит actual key, чтобы re-fire — сбрасываем + setActiveScope
      // тем же значением: useEffect не сработает (scopeKey === last). Поэтому
      // делаем fitBounds inline.
      const m = mapRef.current;
      if (!m) return;
      const cur = useMapStore.getState().activeScope;
      const init = mapInit;
      let bbox: [number, number, number, number] | null | undefined = null;
      if (cur.type === 'farm' && cur.id) {
        bbox = init?.markers.find((mm) => mm.id === cur.id)?.bbox ?? null;
      } else if (cur.type === 'cluster' && cur.id) {
        bbox = init?.cluster_markers.find((c) => c.id === cur.id)?.bbox ?? null;
      } else {
        const all = init?.cluster_markers ?? [];
        if (all.length > 0) {
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for (const c of all) {
            if (!c.bbox) continue;
            const [x1, y1, x2, y2] = c.bbox;
            if (x1 < minX) minX = x1;
            if (y1 < minY) minY = y1;
            if (x2 > maxX) maxX = x2;
            if (y2 > maxY) maxY = y2;
          }
          if (Number.isFinite(minX)) bbox = [minX, minY, maxX, maxY];
        }
      }
      if (bbox) {
        // Founder feedback 2026-05-04: «максимально большой кадр» — same params
        // как в основном fitBounds на смене scope. Поддерживаем синхронно.
        const maxZoom = cur.type === 'farm' ? 15 : cur.type === 'cluster' ? 12 : 9;
        m.fitBounds(
          [
            [bbox[0], bbox[1]],
            [bbox[2], bbox[3]],
          ],
          { padding: 40, maxZoom, duration: 500, essential: true },
        );
      }
    };

    const homeContainer = document.createElement('div');
    homeContainer.className = 'maplibregl-ctrl maplibregl-ctrl-group';
    homeContainer.appendChild(homeBtn);
    const homeControl: maplibregl.IControl = {
      onAdd: () => homeContainer,
      onRemove: () => {
        homeContainer.parentNode?.removeChild(homeContainer);
      },
    };
    mapInstance.addControl(homeControl, 'top-right');

    mapInstance.on('load', () => {
      setupMapLayers(mapInstance, {
        selectedDate: useMapStore.getState().selectedDate,
        // Вставляем data layers под roads-overlay чтобы названия дорог поверх данных.
        beforeId: 'roads-overlay',
      });
      setMap(mapInstance);
    });

    // Track current user-level (1..12, inverted) → useFarmChips для level-based visibility.
    // Используем production helper userLevelFromMap() который маппит ML zoom через
    // pre-calibrated thresholds (zoomLevels.ts).
    const updateUserLevel = () => {
      setUserLevel(userLevelFromMap(mapInstance));
    };
    mapInstance.on('zoom', updateUserLevel);
    mapInstance.once('load', updateUserLevel);

    mapRef.current = mapInstance;

    // ResizeObserver: критично для случаев когда container стартует маленьким
    // (flex layout race) — без этого MapLibre рендерит тайлы только в первоначальный
    // small viewport. QA finding (2026-04-30): map был 500x500 в центре canvas
    // 1277x865 потому что resize() не вызывался при росте контейнера.
    const resizeObserver = new ResizeObserver(() => {
      try {
        mapInstance.resize();
      } catch {
        // map уже destroyed — игнорируем
      }
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    // Дополнительно: forced resize после load чтобы MapLibre перечитал actual размер.
    mapInstance.once('load', () => {
      // RAF чтобы layout успел settle
      requestAnimationFrame(() => mapInstance.resize());
    });

    return () => {
      resizeObserver.disconnect();
      mapInstance.remove();
      mapRef.current = null;
      setMap(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Click handler — read field properties from MVT feature.
  //
  // BUGFIX AK-63: раньше использовали `map.on('click', handler)` (без layerIds)
  // → MapLibre не наполняет `e.features` для глобального click-event'а
  // (features заполняются только для layer-filtered подписки или через
  // queryRenderedFeatures). Из-за этого `feats.length === 0` всегда true
  // и popup никогда не открывался.
  //
  // Решение: подписываемся через `map.on('click', layerIds, handler)` со списком
  // hit-layers. MapLibre сам делает hit-test и кладёт feature в `e.features`.
  // Дополнительно: ловим click по карте (без layer-фильтра) для close-on-outside.
  useEffect(() => {
    if (!map) return;

    const fieldClickHandler = (e: MapLayerMouseEvent) => {
      // queryRenderedFeatures даёт более стабильный fallback — в e.features
      // приходят только features layer-фильтрованной подписки, но если
      // несколько hit-layers перекрываются (cropFill + outline), можно
      // упустить тот, у которого свойства полнее. Запрашиваем явно.
      let layersForHit: string[] = [];
      try {
        layersForHit = FIELDS_HIT_LAYERS.filter((id) => map.getLayer(id));
      } catch {
        return; // map removed, ignore late click
      }
      const feats =
        layersForHit.length > 0
          ? map.queryRenderedFeatures(e.point, { layers: layersForHit })
          : (e.features ?? []);
      if (!feats.length) return;
      const f = feats[0];
      const p = (f.properties ?? {}) as Record<string, unknown>;
      const detail: FieldDetail = {
        id: String(p.id ?? f.id ?? ''),
        field_number: typeof p.field_number === 'string' ? p.field_number : null,
        current_crop_id: typeof p.current_crop_id === 'string' ? p.current_crop_id : null,
        current_phase:
          p.current_phase === 'planned' ||
          p.current_phase === 'growing' ||
          p.current_phase === 'harvested'
            ? p.current_phase
            : null,
        area_ha: typeof p.area_ha === 'number' ? p.area_ha : null,
        avg_ndvi: typeof p.avg_ndvi === 'number' ? p.avg_ndvi : null,
        cv_ndvi: typeof p.cv_ndvi === 'number' ? p.cv_ndvi : null,
      };
      if (!detail.id) return;
      useMapStore.getState().setSelectedFieldId(detail.id);
      applySelectedFieldHighlight(map, detail.id);
      onFieldClick?.(detail, { x: e.point.x, y: e.point.y });
    };

    // Click по карте без feature → close popover (click-outside).
    const mapClickHandler = (e: MapLayerMouseEvent) => {
      let layersForHit: string[] = [];
      try {
        layersForHit = FIELDS_HIT_LAYERS.filter((id) => map.getLayer(id));
      } catch {
        return; // map removed, ignore
      }
      if (layersForHit.length === 0) return;
      const feats = map.queryRenderedFeatures(e.point, { layers: layersForHit });
      if (feats.length === 0) {
        useMapStore.getState().setSelectedFieldId(null);
        applySelectedFieldHighlight(map, null);
      }
    };

    const cursorEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    const cursorLeave = () => {
      map.getCanvas().style.cursor = '';
    };

    const wireUp = () => {
      // Guard: если map уже removed (например, race из-за быстрого
      // unmount/remount), getLayer падает с TypeError. Молча выходим —
      // следующий маунт перерегистрирует handlers.
      let layersForHit: string[] = [];
      try {
        layersForHit = FIELDS_HIT_LAYERS.filter((id) => map.getLayer(id));
      } catch {
        return false;
      }
      if (layersForHit.length === 0) return false;
      // ВАЖНО: layer-filtered подписка → e.features наполняется MapLibre'ом.
      map.on('click', layersForHit, fieldClickHandler);
      // Generic click — для close-on-outside; срабатывает ДО layer-handler'а
      // только при отсутствии feature, поскольку проверяем queryRenderedFeatures.
      map.on('click', mapClickHandler);
      for (const id of layersForHit) {
        map.on('mouseenter', id, cursorEnter);
        map.on('mouseleave', id, cursorLeave);
      }
      return true;
    };

    let wired = wireUp();
    let onceLoad: (() => void) | null = null;
    if (!wired) {
      // setupMapLayers ещё не отработал — ждём load + idle.
      onceLoad = () => {
        wired = wireUp();
      };
      map.once('idle', onceLoad);
    }

    // Esc → close popover.
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        useMapStore.getState().setSelectedFieldId(null);
        applySelectedFieldHighlight(map, null);
      }
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
      // Founder QA 2026-05-05 (TC-13 v2): cleanup function мог упасть
      // на `FIELDS_HIT_LAYERS.filter((id) => map.getLayer(id))` если map
      // уже был remove()'нут (style = undefined → getLayer throws TypeError).
      // Это происходило при navigate'е /map → /fields/{uuid} через popover
      // «Подробнее»: React unmount запускал cleanup, но к этому моменту
      // другой effect уже вызвал map.remove(). Async TypeError пробрасывался
      // в Error Boundary → «Something went wrong» белая страница.
      //
      // Fix: оборачиваем весь cleanup в try/catch — если map.style уже
      // invalid, listeners все равно автоматически снимаются map.remove(),
      // ручная отписка не нужна.
      try {
        if (onceLoad) map.off('idle', onceLoad);
      } catch {
        return; // map уже removed, остальное не имеет смысла
      }
      let layersForHit: string[] = [];
      try {
        layersForHit = FIELDS_HIT_LAYERS.filter((id) => map.getLayer(id));
      } catch {
        return; // map.style undefined — все listeners уже cleaned by map.remove()
      }
      try {
        map.off('click', layersForHit, fieldClickHandler);
      } catch {
        // ignore
      }
      try {
        map.off('click', mapClickHandler);
      } catch {
        // ignore
      }
      for (const id of layersForHit) {
        try {
          map.off('mouseenter', id, cursorEnter);
          map.off('mouseleave', id, cursorLeave);
        } catch {
          // ignore
        }
      }
    };
  }, [map, onFieldClick]);

  // Sync highlight filter с store (на случай setSelectedFieldId извне —
  // например из AIInsightsPanel.onSelectField, popover X-кнопки, navigate).
  const selectedFieldId = useMapStore((s) => s.selectedFieldId);
  useEffect(() => {
    if (!map) return;
    applySelectedFieldHighlight(map, selectedFieldId);
  }, [map, selectedFieldId]);

  // Wire up sync hooks
  useNdviModeSync(map, mode);
  // AK-71: scope участвует в deps useDateSync — при смене scope все raster sources
  // (NDVI/contrast/GNR/TCI) обновляют URL с новыми farm_id/cluster_id query params.
  // cloudMask остаётся без scope (full scene per founder requirement).
  useDateSync(
    map,
    selectedDate,
    activeScope.type === 'company' ? null : { type: activeScope.type, id: activeScope.id },
  );
  useCropPatternsSync(map, crops.data);
  useCropFillExpressionSync(map, crops.data);
  useFocusModeSync(map, focusedCropId);
  useCropIconsToggleSync(map, cropIconsOn);
  useCropNameToggleSync(map, cropNameOn);
  useFieldNumberToggleSync(map, fieldNumberOn);
  useFieldAreaToggleSync(map, fieldAreaOn);
  useCropFillSync(map, cropColorFillOn);
  useTCIToggleSync(map, showTCI);
  useCloudMaskToggleSync(map, showCloudMask);
  useBasemapSync(map, basemap);

  return (
    <div
      ref={containerRef}
      className={cn('absolute inset-0', className)}
      data-map-canvas
    />
  );
}
